import { getClientBranchId, getObjectLength, useAppContext } from "application";
import { Skeleton } from "components";
import { catalogApi, cookie } from "implementations";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProductCardDto } from "typing";
import { AlsoSeeUI } from "./ui";

interface AlsoSeeProps {
  idProduct: string;
  idSection: string;
  isBlackTheme?: boolean;
}

const AlsoSee = ({ idProduct, idSection, isBlackTheme }: AlsoSeeProps) => {
  const { isClientTablet, isClientMobile } = useAppContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAlsoSeeCollapsed, setIsAlsoSeeCollapsed] = useState(false);
  const pageSize = isClientMobile ? 14 : 15;
  const [totalProductCardsRows, setTotalProductCardsRows] = useState(0);

  const branchId = getClientBranchId(cookie);

  const toggleShowCollapsible = useCallback(() => {
    setIsAlsoSeeCollapsed((state) => !state);
  }, []);

  const alsoSeeContainerRef = useRef<HTMLDivElement>(null);

  const scrollToAlsoSeeContainer = useCallback(() => {
    if (alsoSeeContainerRef?.current) {
      const yPosition = alsoSeeContainerRef.current.offsetTop - 200;
      window.scrollTo({
        top: yPosition,
        behavior: "smooth",
      });
    }
  }, []);

  const { data: relatedCategoryData, isLoading: isLoadingRelatedCategory } =
    catalogApi.getRelatedCategory(idProduct, branchId);

  useEffect(() => {
    setSelectedCategoryId(relatedCategoryData?.[0]?.category as number);
  }, [relatedCategoryData]);

  const { data: relatedProducts, isLoading: isLoadingRelatedProducts } =
    catalogApi.getRelatedProducts(
      idProduct,
      branchId,
      String(selectedCategoryId || relatedCategoryData?.[0]?.category),
      currentPage,
      pageSize,
      !!(selectedCategoryId || relatedCategoryData?.[0]?.category)
    );

  const productIdsList = relatedProducts?.map(
    (relatedProduct) => relatedProduct?.code || "0"
  ) as string[];

  const { data: products, isLoading: isLoadingProductCards } =
    catalogApi.getProductCards(
      Number(branchId),
      productIdsList,
      currentPage,
      pageSize,
      selectedCategoryId,
      !!(selectedCategoryId && productIdsList?.length)
    );

  useEffect(() => {
    if (products) {
      setTotalProductCardsRows(
        products?.[0]?.headers?.totalResults || productIdsList?.length
      );
    }
  }, [products, productIdsList?.length]);

  const handleOnClickOnCategoryItem = useCallback(
    (categoryId: number) => {
      setSelectedCategoryId(categoryId);
      setCurrentPage(1);
      scrollToAlsoSeeContainer();
    },
    [scrollToAlsoSeeContainer]
  );

  const isToShowPaginator =
    (Array.isArray(products) && products?.length > 11) || currentPage > 1;

  if (isLoadingRelatedCategory) {
    return (
      <Skeleton
        type="basic"
        count={4}
        height={100}
        width={isClientMobile ? 360 : isClientTablet ? 480 : 760}
      />
    );
  }

  if (!isLoadingRelatedCategory && !relatedCategoryData?.length) {
    return null;
  }

  return (
    <AlsoSeeUI
      idSection={idSection}
      isBlackTheme={!!isBlackTheme}
      isLoadingRelatedProducts={isLoadingRelatedProducts}
      isLoadingProductCards={isLoadingProductCards}
      productsData={products as ProductCardDto[]}
      relatedCategoryData={relatedCategoryData}
      isClientTablet={isClientTablet}
      alsoSeeContainerRef={alsoSeeContainerRef}
      isAlsoSeeCollapsed={isAlsoSeeCollapsed}
      totalProductCardsRows={totalProductCardsRows}
      selectedCategoryId={selectedCategoryId}
      isToShowPaginator={isToShowPaginator}
      pageSize={pageSize}
      currentPage={currentPage}
      productIdsListLength={getObjectLength(productIdsList)}
      onToggleShowCollapsible={toggleShowCollapsible}
      handleOnClickOnCategoryItem={handleOnClickOnCategoryItem}
      onSetCurrentPage={setCurrentPage}
      onScrollToAlsoSeeContainer={scrollToAlsoSeeContainer}
    />
  );
};

export default AlsoSee;
